/**
 * 来場者ホーム画面
 * 来場者向け機能への遷移導線を提供します
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Modal, Pressable, ScrollView, Text, View, useWindowDimensions } from '../../../shared/components/WebPrimitives';
import webStorage from '../../../shared/services/webStorage';
import { VISITOR_POINT_REFRESH_FAILED_ERROR_MESSAGE } from '../constants';
import { confirmAction } from '../../../shared/components/ConfirmDialog';
import TextSizeControl from '../../../shared/components/TextSizeControl';
import EmergencyNoticeModal from '../components/EmergencyNoticeModal';
import NoticeToast from '../components/NoticeToast';
import VisitorTermsModal from '../components/VisitorTermsModal';
import { startGuidedTour, useIsGuidedTourRunning } from '../components/VisitorGuidedTour';
import VisitorBottomSwitchBar from '../components/VisitorBottomSwitchBar';
import VisitorPreparingNotice from '../components/VisitorPreparingNotice';
import StampIslandMap from '../components/StampIslandMap';
import VotingFestAnnouncementModal from '../components/VotingFestAnnouncementModal';
import VotingFestModal from '../components/VotingFestModal';
import VotingFestResultsModal from '../components/VotingFestResultsModal';
import useVisitorTermsReconsent from '../hooks/useVisitorTermsReconsent';
import useVisitorSession from '../hooks/useVisitorSession';
import { visitorLogout } from '../services/visitorAuthService';
import { visitorAcknowledgeEmergencyNotice } from '../services/visitorEmergencyNoticeService';
import { visitorGetHomeBootstrap } from '../services/visitorHomeBootstrapService';
import {
  VISITOR_VOTING_FEST_ALREADY_VOTED_CODE,
  visitorGetVotingFestAnnouncement,
  visitorGetVotingFestStatus,
  visitorSubmitVoteFest,
} from '../services/visitorVotingFestService';
import { loadLocalEventVisits } from '../services/visitorStampCacheService';
import {
  clearPendingStampCelebrations,
  loadPendingStampCelebrations,
} from '../services/visitorStampCelebrationService';
import {
  visitorGetHiddenTitles,
  type VisitorHiddenTitle,
} from '../services/visitorHiddenTitleService';
import { clearVisitorSession, saveVisitorSession } from '../services/visitorSessionService';
import {
  resolveHeroLevelLabel,
  resolveHeroProgressRatio,
  resolveHeroRankPoint,
  resolveHeroStage,
  resolveNextHeroStage,
} from '../utils/heroProgression';
import {
  buildStampSummaryByIslandId,
  mergeEventVisitsWithPendingCelebrations,
  resolveNewlyStampedIslandIds,
  resolvePendingStampCelebrationIslandIds,
} from '../utils/stampBook';
import {
  getTutorialSeenKey,
  getVotingAnnouncementSeenKey,
  getVotingResultsSeenKey,
  VISITOR_TUTORIAL_CONTENT_VERSION,
} from '../utils/visitorContentState';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import AdventureMenuCard from '../../../shared/components/AdventureMenuCard';
import ScreenLayout from '../../../shared/components/ScreenLayout';
import PointCampaignBand from '../components/PointCampaignBand';
import { tourAnchor } from '../../../shared/components/guidedTourAnchor';
import { STAMP_ISLANDS } from '../../../shared/constants/stampIslands';
import type { HomeFeatureVisibilityKey } from '../../../shared/config/appPublicSettings';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import { logError, logWarn } from '../../../shared/utils/logger';

/** 屋台評価scanから戻る際のホームスクロール位置キー */
const HOME_SCROLL_RESTORE_STORAGE_KEY =
  '@ikomasai/home_scroll_restore/stall-rating';

/**
 * 来場者ホーム画面
 * @param {Object} props - プロパティ
 * @param {Object} props.navigation - ナビゲーション
 * @param {Object} props.route - ルート
 * @returns {JSX.Element} 来場者ホーム画面
 */
const VisitorHomeScreen = ({ navigation, route }) => {
  /** 画面サイズ */
  const { height } = useWindowDimensions();

  /** URL状態またはlocalStorageからセッションを復元 */
  const { isSessionReady, sessionToken } = useVisitorSession(navigation, route);

  /** 公開来場者進行設定 */
  const {
    settings,
    isLoading: isPublicSettingsLoading,
  } = usePublicAppSettings();

  /** 管理設定に応じてホームの機能ボタンを表示するか判定する。 */
  const shouldShowHomeFeature = (featureKey: HomeFeatureVisibilityKey): boolean => (
    settings.homeFeatureVisibility[featureKey]
  );

  /** 有効規約への再同意状態 */
  const {
    terms: reconsentTerms,
    isBlocking: isTermsReconsentBlocking,
    isChecking: isTermsReconsentChecking,
    isSubmitting: isTermsReconsentSubmitting,
    isTermsAccepted: isReconsentTermsAccepted,
    errorMessage: termsReconsentErrorMessage,
    hasLoadError: hasTermsReconsentLoadError,
    handleToggleTermsAccepted: handleToggleReconsentTermsAccepted,
    handleAcceptTerms: handleAcceptReconsentTerms,
    handleRetryConsentStatus: handleRetryTermsReconsentStatus,
  } = useVisitorTermsReconsent(sessionToken);

  /** ログイン時のニックネーム（セッション復元前はプレースホルダーを表示） */
  const [nickname, setNickname] = useState('ゲスト');

  /** 現在ポイント */
  const [currentPoint, setCurrentPoint] = useState(0);

  /** 勇者ランク判定用EXP（セッション復元後に更新） */
  const [heroRankPoint, setHeroRankPoint] = useState(0);

  /** 最新お知らせ一覧 */
  const [notices, setNotices] = useState([]);

  /** ポイント再取得エラー */
  const [pointRefreshErrorMessage, setPointRefreshErrorMessage] = useState('');

  /** ポイント読み込み状態 */
  const [pointLoadStatus, setPointLoadStatus] = useState('loading');

  /** 現在ステージ */
  const currentStage = useMemo(
    () => resolveHeroStage(heroRankPoint, settings.heroRanks),
    [heroRankPoint, settings.heroRanks],
  );

  /** 次ステージ */
  const nextStage = useMemo(
    () => resolveNextHeroStage(heroRankPoint, settings.heroRanks),
    [heroRankPoint, settings.heroRanks],
  );

  /** 成長ゲージ進捗 */
  const progressRatio = useMemo(
    () => resolveHeroProgressRatio(heroRankPoint, settings.heroRanks),
    [heroRankPoint, settings.heroRanks],
  );

  /** 最新お知らせ */
  const latestNotice = notices[0] ?? null;

  /** 低い端末高さか */
  const isCompactHeight = height < 760;

  /** スタンプ帳: 読み込み状態 */
  const [stampLoadStatus, setStampLoadStatus] = useState('loading');

  /** スタンプ帳: エラー */
  const [stampErrorMessage, setStampErrorMessage] = useState('');

  /** スタンプ帳: 島別進捗 */
  const [stampSummaryByIslandId, setStampSummaryByIslandId] = useState({});

  /** スタンプ帳: 新規押印島 */
  const [highlightedStampIslandIds, setHighlightedStampIslandIds] = useState([]);

  /** 解除済み隠し称号（未解除定義はRPCから返らない） */
  const [hiddenTitles, setHiddenTitles] = useState<VisitorHiddenTitle[]>([]);

  /** 来場者ID（セッション復元後に設定） */
  const [visitorId, setVisitorId] = useState('');

  /** トースト表示用お知らせ */
  const [toastNotice, setToastNotice] = useState(null);

  /** 今回のホーム表示で確認待ちの公開中緊急お知らせキュー */
  const [publishedEmergencyNotices, setPublishedEmergencyNotices] = useState([]);

  /** 緊急/重要なお知らせの初回確認が完了したか */
  const [
    isEmergencyNoticeCheckComplete,
    setIsEmergencyNoticeCheckComplete,
  ] = useState(false);

  /** 投票フェス: 表示状態 */
  const [votingFestState, setVotingFestState] = useState({
    isVisible: false,
    isEnabled: false,
    questionText: '',
    options: [],
    showResults: false,
    hasVoted: false,
    contentVersion: 'unconfigured',
    winningOption: null,
  });

  /** 投票フェス: 状態取得の進行状態 */
  const [votingFestLoadStatus, setVotingFestLoadStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  /** 投票フェス: 状態確認を完了した来場者ID */
  const [votingFestCheckedVisitorId, setVotingFestCheckedVisitorId] = useState('');

  /** 投票フェス: 状態取得失敗時の案内 */
  const [votingFestLoadErrorMessage, setVotingFestLoadErrorMessage] = useState('');

  /** 投票フェス: 状態取得の再試行回数 */
  const [votingFestRetryCount, setVotingFestRetryCount] = useState(0);

  /** 投票状態の確定前は未回答の可能性を捨てず、ホーム操作を待機させる */
  const isVotingFestStatusCheckPending = Boolean(
    sessionToken
    && (
      !visitorId
      || votingFestCheckedVisitorId !== visitorId
      || votingFestLoadStatus === 'idle'
      || votingFestLoadStatus === 'loading'
    )
  );

  /** 投票結果モーダル表示状態 */
  const [showVotingResults, setShowVotingResults] = useState(false);

  /** 既読確認が完了した投票結果のキー（強制表示告知を投票結果より後に出すために使う） */
  const [checkedVotingResultsKey, setCheckedVotingResultsKey] = useState('');

  /** 強制表示告知（非表示中は isEnabled=false） */
  const [votingAnnouncement, setVotingAnnouncement] = useState({
    isEnabled: false,
    title: '',
    body: '',
    imageUrl: '',
    linkUrl: '',
    linkLabel: '',
    menuLabel: '',
    contentVersion: 'disabled',
  });

  /** 強制表示告知モーダル表示状態 */
  const [showVotingAnnouncement, setShowVotingAnnouncement] = useState(false);

  /** 未読のため使い方ガイドを自動で始める予定か（緊急お知らせ・規約再同意が済むまで待つ） */
  const [isTourOfferPending, setIsTourOfferPending] = useState(false);

  /** 使い方ガイド（画面をまたぐガイド）の実行中か */
  const isGuidedTourRunning = useIsGuidedTourRunning();

  /**
   * 使い方ガイドが投票系のモーダルより優先されている間か
   * 自動開始の待ち（予定）と実行中の両方を含め、ガイドより先に投票モーダルが出る隙間を作らない。
   * ガイドが終わった瞬間にfalseになり、投票ONかつ未投票なら投票モーダルが続けて出る。
   */
  const isTourBlockingHome = isTourOfferPending || isGuidedTourRunning;

  /** セッション・緊急確認は他のホームモーダルより先に単独表示する */
  const isPrimaryHomePrerequisiteCheckPending = Boolean(
    !isSessionReady
    || (sessionToken && !isEmergencyNoticeCheckComplete),
  );

  /** 投票状態の待機は緊急・規約・チュートリアルが無い場合だけ単独表示する */
  const isVotingFestPrerequisiteModalVisible = Boolean(
    isVotingFestStatusCheckPending
    && !isPrimaryHomePrerequisiteCheckPending
    && publishedEmergencyNotices.length === 0
    && !isTermsReconsentBlocking
    && !isTourBlockingHome,
  );

  /** ホーム利用前の確認モーダルを同時に1件だけ表示する */
  const isHomePrerequisiteCheckPending = Boolean(
    isPrimaryHomePrerequisiteCheckPending
    || isVotingFestPrerequisiteModalVisible,
  );

  /** 投票結果表示チェック済みキー（同一versionの重複チェックを防ぐ） */
  const checkedVotingResultsKeyRef = useRef('');

  /** 強制表示告知の表示チェック済みキー（同一versionの重複チェックを防ぐ） */
  const checkedVotingAnnouncementKeyRef = useRef('');

  /** スタンプ帳の初回読み込み完了フラグ */
  const hasLoadedStampBookRef = useRef(false);

  /** このマウントで一度でもホーム集約データの取得に成功したか（focus・retry再取得時の暗転/ちらつき抑止に使う） */
  const hasHomeBootstrapSucceededRef = useRef(false);

  /** 初回成功後のバックグラウンド再取得でpt取得が失敗したか（前回表示値は保持したまま案内する） */
  const [isPointBackgroundRefreshFailed, setIsPointBackgroundRefreshFailed] = useState(false);

  /** 初回成功後のバックグラウンド再取得でスタンプ状況の取得が失敗したか（前回表示値は保持したまま案内する） */
  const [isStampBackgroundRefreshFailed, setIsStampBackgroundRefreshFailed] = useState(false);

  /** ホーム本文のスクロール操作 */
  const homeScrollViewRef = useRef(null);

  /** 直近のホーム本文スクロール位置 */
  const homeScrollTopRef = useRef(0);

  /** 直近スタンプ帳進捗 */
  const previousStampSummaryByIslandIdRef = useRef({});

  /** mount 中フラグ */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** チュートリアル確認済みフラグ（visitorId 変化時の重複チェック防止） */
  const hasTutorialCheckedRef = useRef(false);

  /**
   * チュートリアル既読判定が完了したか
   * 投票フェス自動表示の判定を、チュートリアル表示要否の確定後まで待ち合わせるために使う
   */
  const [isTutorialCheckComplete, setIsTutorialCheckComplete] = useState(false);

  useEffect(() => {
    if (!visitorId || hasTutorialCheckedRef.current) {
      return;
    }

    hasTutorialCheckedRef.current = true;

    /**
     * 来場者IDに紐づくチュートリアル表示判定を行う
     * @returns {Promise<void>}
     */
    const checkTutorialSeen = async () => {
      try {
        /** 既読フラグ */
        const seen = await webStorage.getItem(
          getTutorialSeenKey(visitorId, VISITOR_TUTORIAL_CONTENT_VERSION),
        );

        if (!seen) {
          setIsTourOfferPending(true);
        }
      } catch (error) {
        // 読み込み失敗時は使い方ガイドを自動で始めない
        logWarn('チュートリアル既読状態を読み込めなかったため、使い方ガイドを自動で始めません。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      } finally {
        setIsTutorialCheckComplete(true);
      }
    };

    checkTutorialSeen();
  }, [visitorId]);

  useEffect(() => {
    // 公開設定（管理画面の歓迎文）が揃い、緊急お知らせ・規約再同意が済んでから始める。
    // startGuidedTour は同期で実行中になるため、予定を下ろす同じ描画で isGuidedTourRunning が立つ。
    // ホームの再マウント（ガイドの最後にホームへ戻る時）で再び予定が立っても、実行中・自動開始済みなら何もしない。
    if (
      !isTourOfferPending
      || isPublicSettingsLoading
      || !isEmergencyNoticeCheckComplete
      || publishedEmergencyNotices.length > 0
      || isTermsReconsentBlocking
    ) {
      return;
    }
    startGuidedTour({ visitorId, trigger: 'auto' });
    setIsTourOfferPending(false);
  }, [
    isEmergencyNoticeCheckComplete,
    isPublicSettingsLoading,
    isTermsReconsentBlocking,
    isTourOfferPending,
    publishedEmergencyNotices.length,
    visitorId,
  ]);

  /** 取得済み称号一覧 */
  const unlockedTitleNames = useMemo(() => {
    return STAMP_ISLANDS
      .filter((island) => stampSummaryByIslandId[island.id]?.isCompleted)
      .map((island) => island.titleName);
  }, [stampSummaryByIslandId]);

  useEffect(() => {
    if (highlightedStampIslandIds.length === 0) {
      return;
    }

    /** NEW表示解除タイマー */
    const clearHighlightTimer = setTimeout(() => {
      setHighlightedStampIslandIds([]);
    }, 5000);

    return () => {
      clearTimeout(clearHighlightTimer);
    };
  }, [highlightedStampIslandIds]);

  /**
   * ホーム画面の初期表示データ（セッション/最新お知らせ/緊急お知らせ/公開企画/スタンプ状況）を
   * 集約RPC 1本でまとめて取得し、pt・お知らせ・スタンプ帳の各状態へ反映する
   * @param {{ forceRefresh?: boolean }} [options={}] - forceRefresh指定でTTLキャッシュを無視する
   * @returns {Promise<void>}
   */
  const loadHomeBootstrapData = useCallback(async (options: { forceRefresh?: boolean } = {}) => {
    if (!sessionToken) {
      return;
    }

    /** このマウントで一度でも取得成功済みか（focus・retryどちらの再取得かに関わらず判定する） */
    const hasSucceededBefore = hasHomeBootstrapSucceededRef.current;

    // 初回（未成功）時は全画面待機のfail-closed挙動を維持するため読み込み中状態へ戻す。
    // 成功済み後の再取得では前回表示のまま裏で更新し、暗転・ちらつきを起こさない。
    if (!hasSucceededBefore) {
      setPointLoadStatus('loading');
      setPointRefreshErrorMessage('');
      setIsEmergencyNoticeCheckComplete(false);
      setStampLoadStatus('loading');
      setStampErrorMessage('');
    }
    setIsPointBackgroundRefreshFailed(false);
    setIsStampBackgroundRefreshFailed(false);

    try {
      const [bootstrap, pendingStampCelebrations, unlockedHiddenTitles] = await Promise.all([
        visitorGetHomeBootstrap(sessionToken, { forceRefresh: options.forceRefresh }),
        loadPendingStampCelebrations(sessionToken),
        visitorGetHiddenTitles(sessionToken).catch((error) => {
          logError('隠し称号の取得に失敗したため、スタンプ帳だけを表示します。', {
            errorMessage: error instanceof Error ? error.message : String(error),
          });
          return [];
        }),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      const { session } = bootstrap;

      if (!session.isValid) {
        try {
          await clearVisitorSession();
        } catch (clearError) {
          logError('来場者セッション削除に失敗しました。', {
            errorMessage: clearError instanceof Error ? clearError.message : String(clearError),
          });
        }

        navigation.reset({
          index: 0,
          routes: [{ name: VISITOR_ROUTES.LOGIN, params: { sessionLost: session.isLost } }],
        });
        return;
      }

      setVisitorId(session.visitorId);
      setNickname(session.nickname);
      setCurrentPoint(session.currentPoint);
      setHeroRankPoint(resolveHeroRankPoint(session.currentPoint, session.maxReachedPoint));

      try {
        await saveVisitorSession({
          visitorId: session.visitorId,
          nickname: session.nickname,
          sessionToken,
          sessionExpiresAt: session.sessionExpiresAt,
        });
      } catch (saveError) {
        // 取得は成功しており表示中のセッションも有効なため、端末への書き戻し失敗でpt・スタンプ帳を失敗表示にしない。
        logWarn('来場者セッションの更新保存に失敗しました。', {
          errorMessage: saveError instanceof Error ? saveError.message : String(saveError),
        });
      }

      if (!isMountedRef.current) {
        return;
      }

      setPointLoadStatus('success');
      setNotices(bootstrap.notices);
      setPublishedEmergencyNotices(bootstrap.emergencyNotices);

      /** ローカル保存済みスタンプ履歴 */
      const localEventVisits = await loadLocalEventVisits(session.visitorId);

      /** 今回の島別進捗 */
      const knownEventVisits = mergeEventVisitsWithPendingCelebrations(
        bootstrap.eventVisits,
        localEventVisits,
      );
      const displayEventVisits = mergeEventVisitsWithPendingCelebrations(
        knownEventVisits,
        pendingStampCelebrations,
      );
      const stableSummaryByIslandId = buildStampSummaryByIslandId(
        bootstrap.events,
        knownEventVisits,
        settings.islandThresholds,
      );
      const nextSummaryByIslandId = buildStampSummaryByIslandId(
        bootstrap.events,
        displayEventVisits,
        settings.islandThresholds,
      );

      /** 新規押印島ID */
      const diffStampedIslandIds = resolveNewlyStampedIslandIds(
        hasLoadedStampBookRef.current
          ? previousStampSummaryByIslandIdRef.current
          : stableSummaryByIslandId,
        nextSummaryByIslandId,
      );

      /** 画面遷移を跨いだ押印演出対象島ID */
      const pendingStampedIslandIds = resolvePendingStampCelebrationIslandIds(
        bootstrap.events,
        knownEventVisits,
        pendingStampCelebrations,
        settings.islandThresholds,
      );

      /** 実際に演出表示する新規押印島ID */
      const newlyStampedIslandIds = Array.from(new Set([
        ...diffStampedIslandIds,
        ...pendingStampedIslandIds,
      ]));

      previousStampSummaryByIslandIdRef.current = nextSummaryByIslandId;
      hasLoadedStampBookRef.current = true;

      setStampSummaryByIslandId(nextSummaryByIslandId);
      setHiddenTitles(unlockedHiddenTitles);

      if (newlyStampedIslandIds.length > 0) {
        /** 新規押印島一覧 */
        const newlyStampedIslands = STAMP_ISLANDS.filter((island) => {
          return newlyStampedIslandIds.includes(island.id);
        });

        /** 押印済みスタンプ名一覧 */
        const stampedIslandLabels = newlyStampedIslands.map((island) => island.label);

        /** 解放称号一覧 */
        const unlockedStampTitles = newlyStampedIslands.map((island) => island.titleName);

        setHighlightedStampIslandIds(newlyStampedIslandIds);
        setToastNotice({
          kind: 'stamp',
          title: newlyStampedIslandIds.length === 1
            ? `${newlyStampedIslands[0].label}のスタンプを押しました`
            : `${newlyStampedIslandIds.length}つの島でスタンプを押しました`,
          body: `押印: ${stampedIslandLabels.join(' / ')} | 称号: ${unlockedStampTitles.join(' / ')}`,
        });
      }

      if (pendingStampCelebrations.length > 0) {
        try {
          await clearPendingStampCelebrations(sessionToken);
        } catch (clearError) {
          logError('スタンプ獲得演出データの削除に失敗しました。', {
            errorMessage: clearError instanceof Error ? clearError.message : String(clearError),
          });
        }
      }

      if (isMountedRef.current) {
        setStampLoadStatus('success');
      }

      // 取得成功後は、以降の再取得（focus・retry）で全画面待機・暗転を起こさない
      hasHomeBootstrapSucceededRef.current = true;
    } catch (error) {
      logError('ホーム画面情報の取得に失敗しました。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      if (!isMountedRef.current) {
        return;
      }

      if (hasSucceededBefore) {
        // 成功済み後の再取得失敗時は、取得失敗を0pt・0件として表示しない契約を守るため
        // pointLoadStatus / stampLoadStatus は変更せず前回成功値の表示を維持したまま、
        // ステータスカード内へ既存のエラー文言と再試行導線だけを追加表示する。
        setPointRefreshErrorMessage(VISITOR_POINT_REFRESH_FAILED_ERROR_MESSAGE);
        setIsPointBackgroundRefreshFailed(true);
        setStampErrorMessage('スタンプ状況の取得に失敗しました。通信状況を確認してください。');
        setIsStampBackgroundRefreshFailed(true);
      } else {
        setPointLoadStatus('error');
        setPointRefreshErrorMessage(VISITOR_POINT_REFRESH_FAILED_ERROR_MESSAGE);
        setStampLoadStatus('error');
        setStampErrorMessage('スタンプ状況の取得に失敗しました。通信状況を確認してください。');
      }
    } finally {
      if (isMountedRef.current) {
        setIsEmergencyNoticeCheckComplete(true);
      }
    }
  }, [navigation, sessionToken, settings.islandThresholds]);

  /**
   * ポイント情報を画面内で再取得する（キャッシュを無視して強制再取得する）
   * @returns {void}
   */
  const handleRetryVisitorProfile = useCallback(() => {
    void loadHomeBootstrapData({ forceRefresh: true });
  }, [loadHomeBootstrapData]);

  useEffect(() => {
    if (!sessionToken || isPublicSettingsLoading) {
      return undefined;
    }

    void loadHomeBootstrapData();

    // マウント時は直接取得し、ブラウザのタブ・アプリから戻った時（focus）にも最新のホーム情報を取り直す
    // （TTLキャッシュ・in-flight dedupeにより短時間の連続フォーカスでは集約RPCを再実行しない）
    const unsubscribe =
      navigation && typeof navigation.addListener === 'function'
        ? navigation.addListener('focus', () => loadHomeBootstrapData())
        : null;

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [navigation, loadHomeBootstrapData, sessionToken, isPublicSettingsLoading]);

  useEffect(() => {
    // 初回ガイドの表示判定を待って投票状態を取得する。
    // 投票モーダルのvisible条件でガイド表示中は待機し、閉じた直後に自動表示する。
    if (!sessionToken || !visitorId || !isTutorialCheckComplete) {
      return undefined;
    }

    let isActive = true;

    /**
     * 投票フェス状態を取得し、表示要否を判定する
     * @returns {Promise<void>}
     */
    const loadVotingFestState = async () => {
      setVotingFestLoadStatus('loading');
      setVotingFestLoadErrorMessage('');

      try {
        const status = await visitorGetVotingFestStatus(sessionToken);

        if (!isActive) {
          return;
        }

        setVotingFestState({
          isVisible: status.isEnabled && !status.hasVoted,
          isEnabled: status.isEnabled,
          questionText: status.questionText,
          options: status.options,
          showResults: status.showResults,
          hasVoted: status.hasVoted,
          contentVersion: status.contentVersion,
          winningOption: status.winningOption,
        });
        setVotingFestCheckedVisitorId(visitorId);
        setVotingFestLoadStatus('success');
      } catch (error) {
        if (!isActive) {
          return;
        }

        setVotingFestCheckedVisitorId(visitorId);
        setVotingFestLoadStatus('error');
        setVotingFestLoadErrorMessage(
          '投票状態を確認できませんでした。通信状況を確認して再試行してください。',
        );
        logError('投票フェス状態の取得に失敗しました。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    };

    loadVotingFestState();

    return () => {
      isActive = false;
    };
  }, [sessionToken, visitorId, votingFestRetryCount, isTutorialCheckComplete]);

  useEffect(() => {
    if (!sessionToken || !visitorId) {
      return undefined;
    }

    let isActive = true;

    /**
     * 強制表示告知を取得する（投票の有無に関係なくログイン中の全員が対象）
     * @returns {Promise<void>}
     */
    const loadVotingAnnouncement = async () => {
      try {
        const announcement = await visitorGetVotingFestAnnouncement(sessionToken);

        if (isActive) {
          setVotingAnnouncement(announcement);
        }
      } catch (error) {
        // 取得失敗時は告知を出さず、ホーム機能を阻害しない
        logError('強制表示告知の取得に失敗しました。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    };

    loadVotingAnnouncement();

    return () => {
      isActive = false;
    };
  }, [sessionToken, visitorId]);

  useEffect(() => {
    // 次にホームへfocusした時も、投票ONかつ未投票なら投票モーダルを表示し直す。
    if (!sessionToken || !navigation || typeof navigation.addListener !== 'function') {
      return undefined;
    }

    const unsubscribe = navigation.addListener('focus', () => {
      setVotingFestState((previousState) => ({
        ...previousState,
        isVisible: previousState.isEnabled && !previousState.hasVoted,
      }));
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [navigation, sessionToken]);

  /**
   * 投票状態の取得を再試行する
   * @returns {void}
   */
  const handleRetryVotingFestStatus = useCallback(() => {
    setVotingFestCheckedVisitorId('');
    setVotingFestLoadStatus('loading');
    setVotingFestLoadErrorMessage('');
    setVotingFestRetryCount((previousCount) => previousCount + 1);
  }, []);

  /**
   * DBで確定した投票状態をホームへ反映する
   * @param {Object} status - 投票状態取得RPCの戻り値
   * @param {boolean} isVisible - 投票モーダルを表示するか
   * @returns {void}
   */
  const applyVotingFestStatus = useCallback((status, isVisible = false) => {
    setVotingFestState({
      isVisible,
      isEnabled: status.isEnabled,
      questionText: status.questionText,
      options: status.options,
      showResults: status.showResults,
      hasVoted: status.hasVoted,
      contentVersion: status.contentVersion,
      winningOption: status.winningOption,
    });
  }, []);

  useEffect(() => {
    try {
      const storedScrollTop = Number(
        window.sessionStorage.getItem(
          HOME_SCROLL_RESTORE_STORAGE_KEY,
        ),
      );
      if (!Number.isFinite(storedScrollTop) || storedScrollTop <= 0) {
        return undefined;
      }

      window.sessionStorage.removeItem(
        HOME_SCROLL_RESTORE_STORAGE_KEY,
      );
      const restoreFrame = window.requestAnimationFrame(() => {
        homeScrollViewRef.current?.scrollTo({
          animated: false,
          y: storedScrollTop,
        });
      });

      return () => window.cancelAnimationFrame(restoreFrame);
    } catch (error) {
      logWarn('ホームのスクロール位置を復元できませんでした。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      return undefined;
    }
  }, []);

  /**
   * 投票送信処理（モーダルから呼ばれる）
   * ALREADY_VOTED エラーの場合はモーダルを閉じて正常終了扱いにする
   * @param {'a' | 'b' | 'c'} chosenOption - 投票先
   * @returns {Promise<void>}
   */
  const handleSubmitVotingFest = useCallback(async (chosenOption) => {
    try {
      const submitResult = await visitorSubmitVoteFest(
        sessionToken,
        chosenOption,
      );
      applyVotingFestStatus(submitResult.status, false);
    } catch (error) {
      if (error?.code === VISITOR_VOTING_FEST_ALREADY_VOTED_CODE) {
        /** 別端末との同時投票時もclient加算せず、DBの確定状態を再取得する */
        const confirmedStatus = await visitorGetVotingFestStatus(sessionToken);
        applyVotingFestStatus(confirmedStatus, false);
        return;
      }
      throw error;
    }
  }, [applyVotingFestStatus, sessionToken]);

  /**
   * ホーム導線から投票モーダルを手動表示する
   * @returns {void}
   */
  const handleOpenVotingFest = useCallback(() => {
    setVotingFestState((previousState) => ({
      ...previousState,
      isVisible:
        previousState.isEnabled && !previousState.hasVoted,
    }));
  }, []);

  /**
   * 投票結果モーダルを閉じてlocalStorageに既読フラグを保存する
   * @returns {Promise<void>}
   */
  const handleCloseVotingResults = useCallback(async () => {
    setShowVotingResults(false);

    if (!visitorId) {
      return;
    }

    try {
      await webStorage.setItem(
        getVotingResultsSeenKey(
          visitorId,
          votingFestState.contentVersion,
        ),
        '1',
      );
    } catch (error) {
      // 保存失敗時は次回アクセスで再表示されるが許容
      logWarn('投票結果の既読状態を保存できませんでした。次回も表示されます。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  }, [visitorId, votingFestState.contentVersion]);

  /**
   * 投票結果モーダルを手動で表示する
   * @returns {void}
   */
  const handleOpenVotingResults = useCallback(() => {
    setShowVotingResults(true);
  }, []);

  /**
   * 強制表示告知モーダルを閉じてlocalStorageに既読フラグを保存する
   * @returns {Promise<void>}
   */
  const handleCloseVotingAnnouncement = useCallback(async () => {
    setShowVotingAnnouncement(false);

    if (!visitorId) {
      return;
    }

    try {
      await webStorage.setItem(
        getVotingAnnouncementSeenKey(
          visitorId,
          votingAnnouncement.contentVersion,
        ),
        '1',
      );
    } catch (error) {
      // 保存失敗時は次回アクセスで再表示されるが許容
      logWarn('強制表示告知の既読状態を保存できませんでした。次回も表示されます。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  }, [visitorId, votingAnnouncement.contentVersion]);

  /**
   * 強制表示告知モーダルを手動で表示する
   * @returns {void}
   */
  const handleOpenVotingAnnouncement = useCallback(() => {
    setShowVotingAnnouncement(true);
  }, []);

  /**
   * ホーム導線から使い方ガイドを手動表示する
   * @returns {void}
   */
  const handleOpenTutorial = useCallback(() => {
    startGuidedTour({ visitorId, trigger: 'manual' });
  }, [visitorId]);

  /**
   * ホーム位置を保存して屋台評価用scannerへ進む
   * @returns {void}
   */
  const handleOpenStallRatingScanner = useCallback(() => {
    try {
      window.sessionStorage.setItem(
        HOME_SCROLL_RESTORE_STORAGE_KEY,
        String(homeScrollTopRef.current),
      );
    } catch (error) {
      // 保存できない場合も屋台評価導線は継続する
      logWarn('ホームのスクロール位置を保存できませんでした。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }

    navigation.navigate(VISITOR_ROUTES.SCAN, {
      returnTo: VISITOR_ROUTES.HOME,
      scanMode: 'stall-rating',
    });
  }, [navigation]);

  /**
   * ログアウト確認ダイアログを表示してログアウト処理を実行する
   * @returns {Promise<void>}
   */
  const handlePressLogout = useCallback(async () => {
    /**
     * ログアウト処理本体を実行する
     * @returns {Promise<void>}
     */
    const executeLogout = async () => {
      try {
        if (sessionToken) {
          await visitorLogout(sessionToken);
        }
      } catch (logoutError) {
        logError('ログアウトRPCに失敗しました。', {
          errorMessage: logoutError instanceof Error ? logoutError.message : String(logoutError),
        });
        // RPCエラーが出てもローカルセッションは削除してログイン画面へ遷移する
      }

      try {
        await clearVisitorSession();
      } catch (clearError) {
        logError('来場者セッション削除に失敗しました。', {
          errorMessage: clearError instanceof Error ? clearError.message : String(clearError),
        });
      }

      navigation.reset({
        index: 0,
        routes: [{ name: VISITOR_ROUTES.LOGIN }],
      });
    };

    /** ログアウト確認の結果 */
    const confirmed = await confirmAction({
      title: 'ぼうけんを中断する',
      message: 'ログアウトしますか？\nログアウトすると、再度ユーザ名とパスワードでログインが必要です。',
      confirmLabel: 'ログアウト',
      tone: 'danger',
    });
    if (confirmed) {
      await executeLogout();
    }
  }, [navigation, sessionToken]);

  /**
   * 緊急お知らせの❌押下時、確認記録を残して今回の表示キューから取り除く
   * 次にホームへ戻ったときは公開中である限り再取得して表示する
   * @param {string} noticeId - お知らせID
   * @returns {Promise<void>}
   */
  const handleAcknowledgeEmergencyNotice = useCallback(async (noticeId) => {
    try {
      await visitorAcknowledgeEmergencyNotice(sessionToken, noticeId);
    } catch (error) {
      logError('緊急お知らせの既読登録に失敗しました。', {
        noticeId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setPublishedEmergencyNotices((previousNotices) => (
        previousNotices.filter((notice) => notice.noticeId !== noticeId)
      ));
    }
  }, [sessionToken]);

  useEffect(() => {
    // 結果表示 ON、投票モーダル非表示、visitorId 確定済みの場合のみチェックする
    if (
      !visitorId
      || !votingFestState.showResults
      || votingFestState.isVisible
    ) {
      return;
    }

    /** visitor IDと投票コンテンツversionを組み合わせた確認キー */
    const resultsSeenKey = getVotingResultsSeenKey(
      visitorId,
      votingFestState.contentVersion,
    );

    if (checkedVotingResultsKeyRef.current === resultsSeenKey) {
      return;
    }

    checkedVotingResultsKeyRef.current = resultsSeenKey;

    /**
     * 投票結果モーダルの表示要否を判定する（来場者 ID ごとに一度のみ）
     * @returns {Promise<void>}
     */
    const checkVotingResultsSeen = async () => {
      try {
        /** 既読フラグ */
        const seen = await webStorage.getItem(resultsSeenKey);

        if (!seen && isMountedRef.current) {
          setShowVotingResults(true);
        }
      } catch (error) {
        // 読み込み失敗時は表示しない
        logWarn('投票結果の既読状態を読み込めなかったため、自動表示しません。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (isMountedRef.current) {
          setCheckedVotingResultsKey(resultsSeenKey);
        }
      }
    };

    checkVotingResultsSeen();
  }, [
    visitorId,
    votingFestState.contentVersion,
    votingFestState.showResults,
    votingFestState.isVisible,
  ]);

  useEffect(() => {
    if (!visitorId || !votingAnnouncement.isEnabled) {
      return;
    }

    /** visitor IDと告知versionを組み合わせた確認キー */
    const announcementSeenKey = getVotingAnnouncementSeenKey(
      visitorId,
      votingAnnouncement.contentVersion,
    );

    if (checkedVotingAnnouncementKeyRef.current === announcementSeenKey) {
      return;
    }

    checkedVotingAnnouncementKeyRef.current = announcementSeenKey;

    /**
     * 強制表示告知モーダルの自動表示要否を判定する（告知versionごとに一度のみ）
     * @returns {Promise<void>}
     */
    const checkVotingAnnouncementSeen = async () => {
      try {
        /** 既読フラグ */
        const seen = await webStorage.getItem(announcementSeenKey);

        if (!seen && isMountedRef.current) {
          setShowVotingAnnouncement(true);
        }
      } catch (error) {
        // 読み込み失敗時は表示しない
        logWarn('強制表示告知の既読状態を読み込めなかったため、自動表示しません。', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    };

    checkVotingAnnouncementSeen();
  }, [
    visitorId,
    votingAnnouncement.contentVersion,
    votingAnnouncement.isEnabled,
  ]);

  /**
   * スタンプ状況を画面内で再取得する（キャッシュを無視して強制再取得する）
   * @returns {void}
   */
  const handleRetryStampBook = useCallback(() => {
    void loadHomeBootstrapData({ forceRefresh: true });
  }, [loadHomeBootstrapData]);

  return (
    <ScreenLayout
      topSlot={<PointCampaignBand navigation={navigation} />}
      badge="冒険者ホーム"
      bottomBar={(
        <VisitorBottomSwitchBar
          activeRouteName={VISITOR_ROUTES.HOME}
          navigation={navigation}
        />
      )}
      description="pt（ポイント）とスタンプの進み具合を確認しよう！"
      helpItems={[
        '最初に表示されるステータスで、所持pt（ポイント）と勇者ランクを確認できます。',
        'ステータスの下のスタンプ帳から、島の進み具合を確認して対象企画を探せます。',
        'スタンプ帳の下の冒険メニューから、運営が公開している機能を開けます。',
      ]}
      mascotMood="happy"
      mascotPose="guide"
      theme="visitor"
      title="冒険者ホーム"
    >
      <ScrollView
        className="flex-1"
        onScroll={(event) => {
          homeScrollTopRef.current =
            event.currentTarget?.scrollTop ?? 0;
        }}
        ref={homeScrollViewRef}
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-3 pb-3">
          <View
            className={`rounded-none border-4 border-app-quest-night bg-[#FFF8DD] shadow-[4px_4px_0_rgba(22,36,58,0.24)] ${isCompactHeight ? 'p-3' : 'p-4'} ${currentStage.auraClassName}`}
            testID="visitor-status-card"
          >
            <View
              className="flex-row items-start justify-between gap-3 max-[420px]:flex-col"
              testID="visitor-status-summary"
            >
              <View className="min-w-0 flex-1">
                <Text className="text-xs font-medium text-app-text">ようこそ</Text>
                <Text className="mt-1 break-words text-xl font-bold leading-snug text-app-quest-night">
                  {nickname} さん
                </Text>
                {pointLoadStatus === 'success' ? (
                  <View className="mt-3 flex-row items-center gap-3">
                    <View className="overflow-hidden rounded-none border-2 border-app-quest-night bg-[#FFFDF0] p-1">
                      <Image
                        accessibilityLabel={`${currentStage.title} 画像`}
                        resizeMode="contain"
                        source={currentStage.imageSource}
                        style={{ height: 64, width: 64 }}
                        testID="hero-stage-image"
                      />
                    </View>
                    <View className="min-w-0 flex-1">
                      <Text className="text-[11px] font-semibold text-app-text-secondary">現在の勇者ランク</Text>
                      <Text className="mt-1 text-sm font-bold text-app-quest-night">
                        {resolveHeroLevelLabel(heroRankPoint, settings.heroRanks)} / {currentStage.title}
                      </Text>
                      <Text className="mt-1 text-[11px] text-app-text-secondary">
                        {nextStage ? `次の称号: ${nextStage.title}` : '最終称号に到達しています'}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <Text
                    accessibilityLiveRegion="polite"
                    accessibilityRole="status"
                    className="mt-3 text-xs font-semibold text-app-text-secondary"
                  >
                    {pointLoadStatus === 'loading'
                      ? '所持pt（ポイント）を読み込んでいます...'
                      : '勇者ランクを表示できません。'}
                  </Text>
                )}
              </View>
              <View
                className="shrink-0 rounded-none border-2 border-app-quest-night bg-[#FFF4C2] px-3 py-2 max-[420px]:flex-row max-[420px]:items-center max-[420px]:justify-between max-[420px]:gap-2 max-[420px]:self-stretch"
                testID="visitor-point-summary"
                {...tourAnchor('home.points')}
              >
                <Text className="text-xs text-app-text">所持pt（ポイント）</Text>
                {/* 幅320px・文字1.3倍で「12345p／t」と数値の途中で折れないよう、取得できた数値は折らずに見出し側を折る */}
                <Text className={`mt-0.5 text-lg font-bold text-app-quest-night ${pointLoadStatus === 'success' ? 'shrink-0 whitespace-nowrap' : ''}`}>
                  {pointLoadStatus === 'success'
                    ? `${currentPoint}pt`
                    : pointLoadStatus === 'loading'
                      ? '読み込み中...'
                      : '取得できませんでした'}
                </Text>
              </View>
            </View>

            {pointLoadStatus === 'success' ? (
              <View className="mt-3">
                <View className="mb-1.5 flex-row items-center justify-between">
                  <Text className="text-xs font-semibold text-app-text">pt（ポイント）ゲージ</Text>
                  <Text className="text-xs text-app-text">
                    {nextStage ? `次のLvまで ${Math.max(0, nextStage.minPoint - heroRankPoint)} pt` : '最終レベル到達'}
                  </Text>
                </View>
                <View className="h-2.5 rounded-none bg-app-surface-variant">
                  <View
                    className="h-2.5 rounded-none bg-app-quest-gold"
                    style={{ width: `${Math.round(progressRatio * 100)}%` }}
                  />
                </View>
              </View>
            ) : null}

            {pointLoadStatus === 'error' || isPointBackgroundRefreshFailed ? (
              <View className="mt-3 border-2 border-app-danger bg-red-50 p-3">
                <Text className="text-xs font-medium text-app-danger">
                  {pointRefreshErrorMessage}
                </Text>
                <Pressable
                  accessibilityRole="button"
                  className="mt-2 min-h-[44px] items-center justify-center border-2 border-app-quest-night bg-[#FFF4C2] px-3 py-2"
                  onPress={handleRetryVisitorProfile}
                >
                  <Text className="text-xs font-extrabold text-app-quest-night">
                    pt情報を再試行
                  </Text>
                </Pressable>
              </View>
            ) : null}
          </View>

          <View
            className={`rounded-none border-4 border-app-quest-night bg-[#FFF8DD] shadow-[4px_4px_0_rgba(22,36,58,0.24)] ${isCompactHeight ? 'p-3' : 'p-4'}`}
            {...tourAnchor('home.stampBook')}
          >
            <View className="flex-row items-center justify-between gap-3">
              <Text
                aria-level={2}
                className="text-sm font-extrabold text-app-quest-night"
                role="heading"
              >
                スタンプ帳
              </Text>
              <View className="rounded-none border-2 border-app-quest-night bg-[#EAF6FF] px-2.5 py-1">
                <Text className="text-[11px] font-semibold text-app-quest-night">
                  {settings.homeFeatureVisibility.eventsPreparing
                    ? '準備中'
                    : stampLoadStatus === 'loading'
                    ? '読み込み中...'
                    : stampLoadStatus === 'error'
                      ? '取得できませんでした'
                      : `称号 ${unlockedTitleNames.length}/${STAMP_ISLANDS.length}`}
                </Text>
              </View>
            </View>

            <Text className="mt-2 text-xs text-app-text">
              {settings.homeFeatureVisibility.eventsPreparing
                ? '島と企画の公開までお待ちください。'
                : '各島で決められた数の企画に参加すると、その島のスタンプが押されます。島をタップすると企画を確認できます。'}
            </Text>

            {!settings.homeFeatureVisibility.eventsPreparing && stampLoadStatus === 'loading' ? (
              <Text
                accessibilityLiveRegion="polite"
                accessibilityRole="status"
                className="mt-3 text-xs font-semibold text-app-text-secondary"
              >
                スタンプ状況を読み込んでいます...
              </Text>
            ) : null}

            {!settings.homeFeatureVisibility.eventsPreparing && (stampLoadStatus === 'error' || isStampBackgroundRefreshFailed) ? (
              <View className="mt-3 border-2 border-app-danger bg-red-50 p-3">
                <Text className="text-xs font-medium text-app-danger">
                  {stampErrorMessage}
                </Text>
                <Pressable
                  accessibilityRole="button"
                  className="mt-2 min-h-[44px] items-center justify-center border-2 border-app-quest-night bg-[#FFF4C2] px-3 py-2"
                  onPress={handleRetryStampBook}
                >
                  <Text className="text-xs font-extrabold text-app-quest-night">
                    スタンプ状況を再試行
                  </Text>
                </Pressable>
              </View>
            ) : null}

            {settings.homeFeatureVisibility.eventsPreparing ? (
              <VisitorPreparingNotice label="島（企画）" />
            ) : stampLoadStatus === 'success' ? (
              <>
                <View className="mt-3">
                  <StampIslandMap
                    compact={isCompactHeight}
                    highlightedIslandIds={highlightedStampIslandIds}
                    summaryByIslandId={stampSummaryByIslandId}
                    onPressIsland={(islandId) =>
                      navigation.navigate(VISITOR_ROUTES.EVENTS, { islandId })
                    }
                  />
                </View>

                {unlockedTitleNames.length > 0 ? (
                  <View className="mt-3 rounded-none border-2 border-app-quest-night bg-[#FFFDF0] p-2">
                    <Text className="text-[11px] font-semibold text-app-quest-night">獲得した称号</Text>
                    <Text className="mt-1 text-[11px] text-app-text">{unlockedTitleNames.join(' / ')}</Text>
                  </View>
                ) : null}

                {hiddenTitles.length > 0 ? (
                  <View
                    className="mt-3 rounded-none border-2 border-app-quest-gold bg-[#FFF4C2] p-2"
                    testID="visitor-hidden-titles"
                  >
                    <Text className="text-[11px] font-semibold text-app-quest-night">
                      解除した隠し称号
                    </Text>
                    <Text className="mt-1 text-[11px] text-app-text">
                      {hiddenTitles.map((title) => title.name).join(' / ')}
                    </Text>
                  </View>
                ) : null}
              </>
            ) : null}
          </View>

          <Text
            aria-level={2}
            className="pt-1 text-lg font-extrabold text-app-quest-night"
            role="heading"
          >
            冒険メニュー
          </Text>
          <View
            className="flex-row flex-wrap gap-2"
            testID="adventure-menu"
          >
            <AdventureMenuCard
              accessibilityLabel="全ての企画を検索する"
              description={settings.homeFeatureVisibility.eventsPreparing ? '今は準備中です' : 'すべての企画を検索'}
              iconName="map"
              onPress={() => navigation.navigate(VISITOR_ROUTES.EVENTS)}
              testID="events-open-button"
              title="企画をさがす"
            />

            {shouldShowHomeFeature('notices') ? (
              <AdventureMenuCard
                accessibilityLabel="最新のお知らせを開く"
                badge={latestNotice?.isImportant ? '重要' : ''}
                description={latestNotice ? latestNotice.title : '公開中のお知らせはありません'}
                iconName="notice"
                isImportant={Boolean(latestNotice?.isImportant)}
                onPress={() => navigation.navigate(VISITOR_ROUTES.NOTICES)}
                testID="notice-open-button"
                title="最新のお知らせ"
              />
            ) : null}

            {shouldShowHomeFeature('tutorial') ? (
              <AdventureMenuCard
                accessibilityLabel="使い方ガイドを開く"
                anchorProps={tourAnchor('home.guideMenu')}
                description="操作方法をもう一度見る"
                iconName="notice"
                onPress={handleOpenTutorial}
                testID="tutorial-open-button"
                title="使い方ガイド"
              />
            ) : null}

            {votingAnnouncement.isEnabled ? (
              <AdventureMenuCard
                accessibilityLabel={`${votingAnnouncement.menuLabel}を開く: ${votingAnnouncement.title}`}
                description={votingAnnouncement.title}
                iconName="notice"
                onPress={handleOpenVotingAnnouncement}
                testID="voting-announcement-open-button"
                title={votingAnnouncement.menuLabel}
                tone="result"
              />
            ) : null}

            {shouldShowHomeFeature('votingResults') && votingFestState.showResults ? (
              <AdventureMenuCard
                accessibilityLabel="投票の結果はこちら"
                badge="公開中"
                description="投票の結果はこちら"
                iconName="results"
                onPress={handleOpenVotingResults}
                testID="voting-results-open-button"
                title="投票結果"
                tone="result"
              />
            ) : null}

            {shouldShowHomeFeature('voting') && votingFestState.isEnabled && !votingFestState.hasVoted ? (
              <AdventureMenuCard
                accessibilityLabel="企画投票を開く"
                badge="一人一票"
                description="回答してホームへ進む"
                iconName="results"
                onPress={handleOpenVotingFest}
                testID="voting-fest-open-button"
                title="投票する"
                tone="result"
              />
            ) : null}

            {shouldShowHomeFeature('stallRanking') ? (
              <AdventureMenuCard
                accessibilityLabel="屋台ランキング用QRを読み取る"
                description="QRを読み取り評価"
                iconName="stall"
                onPress={handleOpenStallRatingScanner}
                testID="stall-rating-open-button"
                title="屋台ランキング"
              />
            ) : null}

            {shouldShowHomeFeature('ranking') ? (
              <AdventureMenuCard
                accessibilityLabel="ptランキングを見る"
                description="みんなの順位を確認"
                iconName="ranking"
                onPress={() => navigation.navigate(VISITOR_ROUTES.RANKING)}
                testID="ranking-open-button"
                title="ptランキング"
              />
            ) : null}

            {shouldShowHomeFeature('group') ? (
              <AdventureMenuCard
                accessibilityLabel="友達グループを開く"
                description="仲間とptを競う"
                iconName="group"
                onPress={() => navigation.navigate(VISITOR_ROUTES.GROUP)}
                testID="group-open-button"
                title="友達グループ"
              />
            ) : null}

            {shouldShowHomeFeature('campusMap') ? (
              <AdventureMenuCard
                accessibilityLabel="キャンパスマップを開く"
                description="近くの建物を確認"
                iconName="map"
                onPress={() => navigation.navigate(VISITOR_ROUTES.CAMPUS_MAP)}
                testID="campus-map-open-button"
                title="キャンパスマップ"
              />
            ) : null}
          </View>

          <TextSizeControl />

          <Pressable
            accessibilityLabel="ログアウト"
            accessibilityRole="button"
            className="mt-1 min-h-[44px] flex-row items-center justify-center gap-1 self-stretch rounded-none border-2 border-app-quest-night bg-[#FFFDF0] px-3 py-2"
            onPress={handlePressLogout}
            testID="logout-button"
          >
            <Text accessible={false} className="text-sm font-extrabold text-app-quest-night">
              ⎋
            </Text>
            <Text className="text-sm font-extrabold text-app-quest-night">
              ログアウト
            </Text>
          </Pressable>
        </View>
      </ScrollView>

      <NoticeToast
        notice={toastNotice}
        onDismiss={() => setToastNotice(null)}
        onPress={toastNotice?.kind === 'notice'
          ? () => {
            setToastNotice(null);
            navigation.navigate(VISITOR_ROUTES.NOTICES);
          }
          : undefined}
      />

      {/*
        モーダル表示優先度: 緊急/重要なお知らせ > 規約再同意 > 使い方ガイド > 投票状態再試行 > 投票フェス。
        上位モーダルが残っている間は、下位のモーダルを表示しない。
        使い方ガイドはModalではなく#root内の重ね表示のため、上位のModalが開くと#rootごとinertになって一時停止する。
      */}
      <Modal
        accessibilityLabel="ホーム利用前の確認中"
        animationType="fade"
        dismissible={false}
        statusBarTranslucent
        transparent
        visible={isHomePrerequisiteCheckPending}
      >
        <View className="flex-1 items-center justify-center bg-black/60 px-4">
          <View className="border-2 border-app-quest-night bg-[#FFF8DD] px-6 py-5">
            {pointLoadStatus === 'error' && !visitorId ? (
              <>
                <Text
                  accessibilityLiveRegion="assertive"
                  accessibilityRole="alert"
                  className="text-center text-sm font-bold text-app-danger"
                >
                  {pointRefreshErrorMessage}
                </Text>
                <Pressable
                  accessibilityLabel="pt情報を再試行"
                  accessibilityRole="button"
                  className="mt-4 min-h-12 items-center justify-center border-2 border-app-quest-night bg-app-quest-gold px-4 py-3"
                  onPress={handleRetryVisitorProfile}
                >
                  <Text className="text-sm font-bold text-app-quest-night">再試行</Text>
                </Pressable>
              </>
            ) : (
              <Text
                accessibilityLiveRegion="polite"
                accessibilityRole="status"
                className="text-center text-sm font-bold text-app-quest-night"
              >
                ホームを利用するための確認をしています...
              </Text>
            )}
          </View>
        </View>
      </Modal>

      <Modal
        accessibilityLabel="投票状態の確認エラー"
        animationType="fade"
        dismissible={false}
        statusBarTranslucent
        transparent
        visible={
          votingFestLoadStatus === 'error'
          && !isTourBlockingHome
          && !isTermsReconsentBlocking
          && isEmergencyNoticeCheckComplete
          && publishedEmergencyNotices.length === 0
        }
      >
        <View className="flex-1 items-center justify-center bg-black/60 px-4">
          <View className="w-full max-w-md border-2 border-app-quest-night bg-[#FFF8DD] px-6 py-5">
            <Text
              accessibilityLiveRegion="assertive"
              accessibilityRole="alert"
              className="text-center text-sm font-bold text-app-quest-night"
            >
              {votingFestLoadErrorMessage}
            </Text>
            <Pressable
              accessibilityLabel="投票状態の取得を再試行"
              accessibilityRole="button"
              className="mt-4 min-h-12 items-center justify-center border-2 border-app-quest-night bg-app-quest-gold px-4 py-3"
              onPress={handleRetryVotingFestStatus}
            >
              <Text className="text-sm font-bold text-app-quest-night">再試行</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <VisitorTermsModal
        errorMessage={termsReconsentErrorMessage}
        isLoading={isTermsReconsentChecking}
        isSubmitting={isTermsReconsentSubmitting}
        isTermsAccepted={isReconsentTermsAccepted}
        mode="reconsent"
        onAccept={handleAcceptReconsentTerms}
        onLogout={handlePressLogout}
        onRetry={hasTermsReconsentLoadError ? handleRetryTermsReconsentStatus : undefined}
        onToggleTermsAccepted={handleToggleReconsentTermsAccepted}
        terms={reconsentTerms}
        visible={
          isTermsReconsentBlocking
          && isEmergencyNoticeCheckComplete
          && publishedEmergencyNotices.length === 0
        }
      />

      <VotingFestModal
        onSubmit={handleSubmitVotingFest}
        options={votingFestState.options}
        questionText={votingFestState.questionText}
        visible={
          votingFestState.isVisible
          && !isTourBlockingHome
          && !isTermsReconsentBlocking
          && isEmergencyNoticeCheckComplete
          && publishedEmergencyNotices.length === 0
        }
      />

      <VotingFestResultsModal
        onClose={handleCloseVotingResults}
        options={votingFestState.options}
        questionText={votingFestState.questionText}
        winningOption={votingFestState.winningOption}
        visible={
          showVotingResults
          && !isTourBlockingHome
          && !isTermsReconsentBlocking
          && isEmergencyNoticeCheckComplete
          && publishedEmergencyNotices.length === 0
        }
      />

      {/* 強制表示告知は投票状態の確認・投票・投票結果モーダルが済んだ後に表示する */}
      <VotingFestAnnouncementModal
        announcement={votingAnnouncement}
        onClose={handleCloseVotingAnnouncement}
        visible={
          showVotingAnnouncement
          && !isHomePrerequisiteCheckPending
          && !isVotingFestStatusCheckPending
          && votingFestLoadStatus !== 'error'
          && (
            !votingFestState.showResults
            || checkedVotingResultsKey === getVotingResultsSeenKey(visitorId, votingFestState.contentVersion)
          )
          && !votingFestState.isVisible
          && !showVotingResults
          && !isTourBlockingHome
          && !isTermsReconsentBlocking
          && isEmergencyNoticeCheckComplete
          && publishedEmergencyNotices.length === 0
        }
      />

      {/* 緊急/重要なお知らせは最優先で表示し、❌を押すまで閉じられない */}
      <EmergencyNoticeModal
        notice={publishedEmergencyNotices[0] ?? null}
        onAcknowledge={handleAcknowledgeEmergencyNotice}
        visible={publishedEmergencyNotices.length > 0}
      />
    </ScreenLayout>
  );
};

export default VisitorHomeScreen;
